<template>
  <div>
    <v-sheet color="grey-8" class="py-3">
      <div class="d-flex px-4">
        <div class="d-flex align-center" style="width: 800px; max-width: 100%; gap: 8px;">
          <div>搜尋</div>
          <v-text-field
            v-model="searchQ"
            placeholder="輸入姓名或手機或民國生日(例801231)"
            outlined
            hide-details
            dense
            clearable
            @click:clear="clearSearch"
            @keydown.enter="triggerSearch"
          />
          <v-btn icon color="primary" @click="triggerSearch">
            <v-icon>mdi-magnify</v-icon>
          </v-btn>
        </div>
      </div>
    </v-sheet>
    <v-data-table
      :headers="headers"
      :items="careItems"
      height="calc(100vh - 256px)"
      :options.sync="options"
      :loading="loading"
      :server-items-length="totalCares"
      :footer-props="{itemsPerPageOptions: [10, 20, 50, 100]}"
    >
      <template v-slot:item.birthday="{ item }">
        <template v-if="item.birthday">{{ parseInt(item.birthday) * 1000 | formatROCDate }}</template>
      </template>
      <template v-slot:item.content="{ item }">
        <span v-if="item.content && item.content.length > 8">
          {{ item.content.substr(0, 8) }}
          <a @click="showMoreContent(item.content)">...more</a>
        </span>
        <span v-else>
          {{ item.content }}
        </span>
      </template>
      <template v-slot:item.clinic_call="{ item }">
        <span v-if="parseInt(item.clinic_call)" style="color: var(--v-red-6-base);">請聯繫患者</span>
        <span v-else style="color: var(--v-grey-5-base);">不需要聯繫</span>
      </template>
      <template v-slot:item.care_note="{ item }">
        <span>
          {{ (item.care_note && item.care_note.length > 20) ? item.care_note.substr(0, 20) + '...' : item.care_note }}
        </span>
        <v-btn icon color="primary" @click="handleEdit(item)">
          <v-icon dark x-small>
            mdi-pencil
          </v-icon>
        </v-btn>
      </template>
      <template v-slot:item.action="{ item }">
        <v-btn v-if="status === 0" outlined small color="primary" style="margin: 5px 0;" @click="changeStatus(item, 1)">
          設為已處理
        </v-btn>
        <v-btn v-else outlined small color="primary" style="margin: 5px 0;"
               @click="changeStatus(item, 0)">
          設為未處理
        </v-btn>
      </template>

      <template v-slot:item.send="{ item }">
        <v-menu offset-x left>
          <template v-slot:activator="{ on, attrs }">
            <v-btn
              color="primary"
              small
              v-bind="attrs"
              @click="currentEdit = item; getSendChannelInfo($event, on)"
            >
              傳送訊息
            </v-btn>
          </template>
          <v-list dense outlined class="text-body-2">
            <v-list-item
              class="justify-center"
              @click="$refs.SendLineMessageV2.sendMessageDialog = true"
            >
              LINE訊息{{ sendLineSuffix }}
            </v-list-item>
            <v-list-item
              class="justify-center"
              @click="$refs.smsMessageV2.sendMessageDialog = true"
            >
              簡訊{{ sendSmsSuffix }}
            </v-list-item>
          </v-list>
        </v-menu>
      </template>
    </v-data-table>
    <v-dialog v-if="reportBackNoteDialog" max-width="500" v-model="reportBackNoteDialog" persistent>
      <v-card>
        <v-card-title>編輯術後備註</v-card-title>
        <v-card-text>
          <v-textarea v-model="currentEdit.care_note" hide-details outlined no-resize/>
        </v-card-text>

        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="blue" text @click="editReportBackNote">
            ok
          </v-btn>
          <v-btn text @click="reportBackNoteDialog=false">
            {{ $t('GENERAL.cancel') }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
    <v-dialog v-if="showMoreDialog" v-model="showMoreDialog" width="fit-content">
      <v-card width="fit-content" max-width="350" max-height="50vh" class="pa-6 text-pre-line overflow-y-auto" v-text="moreContent"/>
    </v-dialog>
    <SendLineMessageV2
      ref="SendLineMessageV2"
      :btnProps="{text: true, small: true}"
      :patient="{
        patientName: currentEdit?.name,
        patientCode: currentEdit?.patient_code,
        patientPhone: currentEdit?.phone,
      }"
      style="display: none;"
    />
    <SendSmsMessageV2
      v-show="false"
      ref="smsMessageV2"
      :btnProps="{text: true}"
      :patient="{
        patientName: currentEdit?.name,
        patientCode: currentEdit?.patient_code,
        patientPhone: currentEdit?.phone,
      }"
    />
  </div>
</template>

<script>
import { mapActions, mapGetters, mapMutations } from "vuex";
import {
  getCareAPI,
  getChannelQuotaAPI, getMessageSettingAPI,
  getPatientChannelBindStatusAPI,
  getSMSQuotaChannelInfoAPI,
  updateCareAPI
} from "@/api";
import SendLineMessageV2 from "@/components/utils/SendLineMessageV2.vue";
import SendSmsMessageV2 from "@/components/utils/SendSmsMessageV2.vue";

export default {
  name: "HistoryList",
  components: {SendSmsMessageV2, SendLineMessageV2},
  props: {
    status: {
      type: Number,
      required: true,
    },
  },
  data() {
    return {
      careItems: [],
      options: {},
      loading: true,
      totalCares: 0,
      currentEdit: null,
      reportBackNoteDialog: false,
      showMoreDialog: false,
      moreContent: null,
      sendLineSuffix: '',
      sendSmsSuffix: '',

      searchQ: null,
    }
  },
  computed: {
    ...mapGetters([
      'axiosParamData',
      'liffUser',
      'doctorUser',
      'ownChannelLoaded',
      'hasOwnChannel'
    ]),
    headers() {
      const headers = [
        {text: this.$t('PostOperativeCare.patientName'), align: 'center', value: 'name', sortable: false, width: 100},
        {text: this.$t('PostOperativeCare.patientPhone'), align: 'center', value: 'phone', sortable: false, width: 120},
        {text: this.$t('ReserveTable.patientBirth'), align: 'center', value: 'birthday', sortable: false, width: 132},
        {text: this.$t('PostOperativeCare.reserveTime'), align: 'center', value: 'start_time', sortable: false, width: 120},
        {
          text: this.$t('PostOperativeCare.reserveDoctor'),
          align: 'center',
          value: 'doctor_name',
          sortable: false,
          width: 100
        },
        {text: this.$t('PostOperativeCare.reserveNote'), align: 'center', value: 'note', sortable: false, width: 150},
        {
          text: this.$t('PostOperativeCare.reportBackTime'),
          align: 'center',
          value: 'create_time',
          sortable: false,
          width: 150
        },
        {text: this.$t('PostOperativeCare.reportBack'), align: 'center', value: 'content', sortable: false, width: 120},
        {
          text: this.$t('PostOperativeCare.needContact'),
          align: 'center',
          value: 'clinic_call',
          sortable: false,
          width: 150
        },
        {
          text: this.$t('PostOperativeCare.reportBackNote'),
          align: 'center',
          value: 'care_note',
          sortable: false,
          width: 150
        },
        {
          text: this.$t('PostOperativeCare.action'),
          align: 'center',
          value: 'action',
          sortable: false,
          width: 150
        },
      ]
      if (this.doctorUser)
        headers[3] = {
          text: this.$t('PostOperativeCare.reserveClinic'),
          align: 'center',
          value: 'clinic_name',
          sortable: false,
          width: 100
        }

      headers.push({text: '傳送訊息', align: 'center', value: 'send', sortable: false, width: 150})
      return headers
    },
  },
  watch: {
    options: {
      handler() {
        this.getDataFromApi();
      },
      deep: true,
    },
    status: {
      handler() {
        if (this.options.page === 1) {
          this.getDataFromApi();
        } else {
          this.options.page = 1;
        }
      },
      immediate: true, // Fetch data when component is created
    }
  },
  methods: {
    ...mapMutations({
      setOwnChannel: 'setOwnChannel'
    }),
    ...mapActions({
      getUnreadNotice: 'getUnreadNotice',
    }),
    triggerSearch() {
      if (this.options.page === 1) this.getDataFromApi()
      else this.options.page = 1
    },
    clearSearch() {
      this.searchQ = null;
      this.triggerSearch();
    },
    getDataFromApi() {
      this.loading = true
      const {page, itemsPerPage} = this.options
      getCareAPI({
        ...this.axiosParamData,
        reply_status: this.status,
        limit: itemsPerPage,
        page,
        name: this.searchQ
      }).then(res => {
        this.careItems = res.data.data
        this.totalCares = parseInt(res.data.paginate.total)
        this.loading = false
      })
    },
    handleEdit(item) {
      this.currentEdit = JSON.parse(JSON.stringify(item))
      this.reportBackNoteDialog = true
    },
    editReportBackNote() {
      updateCareAPI(this.currentEdit.id, {care_note: this.currentEdit.care_note, ...this.axiosParamData}).then(() => {
        this.currentEdit = null
        this.reportBackNoteDialog = false
        this.getDataFromApi()
      })
    },
    showMoreContent(v) {
      this.moreContent = v
      this.showMoreDialog = true
    },
    changeStatus(item, v) {
      updateCareAPI(item.id, {reply_status: v, ...this.axiosParamData}).then(() => {
        this.getDataFromApi()
        this.getUnreadNotice()
      })
    },
    async getSendChannelInfo(event, on) {
      if (!this.ownChannelLoaded) {
        const res = await getMessageSettingAPI(this.axiosParamData)
        this.setOwnChannel(res.data.own_channel)
      }

      const [bindStatusRes, smsQuotaRes] = await Promise.all([
        getPatientChannelBindStatusAPI(this.currentEdit.patient_code, this.axiosParamData),
        getSMSQuotaChannelInfoAPI(this.axiosParamData).catch(err => err.response)
      ])

      const bindData = bindStatusRes.data
      const smsQuota = smsQuotaRes.data?.quota
      this.sendSmsSuffix = ` - 剩餘${smsQuota}點`

      let lineBindStatus = false

      if (this.hasOwnChannel) {
        lineBindStatus = bindData?.own_channel_bind_status === 1

        if (lineBindStatus) {
          const res3 = await getChannelQuotaAPI(this.axiosParamData)
          const lineQuota = Number(res3.data.quota) - Number(res3.data.total_usage)

          this.sendLineSuffix = lineQuota > 0 ? ' - 可發送' : ' - 額度不足'
        } else {
          this.sendLineSuffix = ' - 未綁定'
        }
      } else {
        lineBindStatus = bindData?.dentco_channel_bind_status === 1
        this.sendLineSuffix = lineBindStatus ? ' - 可發送' : ' - 未綁定'
      }

      await on.click(event)
    }
  }
}
</script>

<style scoped>

</style>

